import { theme } from '@features/executive-report/domain/theme';
import { CampaignOverviewSlide2Options } from '../campaign-overview-2';
import { TextProps, parseContent } from '@features/powerpoint';
import { ValueInput, formatters } from '@utils/formattersV2';

const DRIVER_OFFSET = 1.36;

export type DriverTextsOptions = {
  index: number;
  tagline: TextProps['text'];
  value: ValueInput;
  descriptionText: TextProps['text'];
  metricName: string;
  exposedText: string;
  controlText: string;
};

export const driverTexts = (options: DriverTextsOptions): TextProps[] => [
  {
    text: options.tagline,
    options: {
      fontSize: 12,
      color: theme.colors.white,
      h: 0.3,
      w: 1.88,
      x: 8.65,
      y: 3.12 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: formatters.percentage(options.value, { decimals: 2 }),
    options: {
      fontSize: 28,
      color: theme.colors.white,
      h: 0.57,
      w: 1.81,
      x: 8.88,
      y: 3.36 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: options.descriptionText,
    options: {
      fontSize: 8,
      color: theme.colors.white,
      h: 0.5,
      w: 2.08,
      x: 8.64,
      y: 3.85 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: options.metricName.toUpperCase(),
    options: {
      fontSize: 10,
      color: theme.colors.white,
      bold: true,
      align: 'center',
      h: 0.27,
      w: 2.03,
      x: 10.72,
      y: 3.15 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: 'Exposed'.toUpperCase(),
    options: {
      fontSize: 7,
      color: theme.colors.inmarketGreen,
      bold: true,
      h: 0.22,
      w: 0.9,
      x: 10.79,
      y: 3.55 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: options.exposedText,
    options: {
      fontSize: 18,
      color: theme.colors.white,
      h: 0.4,
      w: 1.26,
      x: 10.77,
      y: 3.82 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: 'Control'.toUpperCase(),
    options: {
      fontSize: 7,
      color: theme.colors.inmarketGreen,
      bold: true,
      h: 0.22,
      w: 0.9,
      x: 11.95,
      y: 3.55 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: options.controlText,
    options: {
      fontSize: 18,
      color: theme.colors.white,
      h: 0.4,
      w: 1.26,
      x: 11.87,
      y: 3.82 + DRIVER_OFFSET * options.index,
    },
  },
];

export const buildDriverTextsOptions = (
  salesMetrics: CampaignOverviewSlide2Options['salesMetrics']
): Omit<DriverTextsOptions, 'index'>[] => [
  {
    tagline: parseContent('<b>MORE</b> <lci>PEOPLE</lci>'),
    value: salesMetrics.penetrationLift.total,
    descriptionText: parseContent(
      'The <b>lift in % of users</b> who made at least one purchase during the measurement period.'
    ),
    metricName: 'Penetration Rate',
    exposedText: formatters.percentage(salesMetrics.exposedPenetration.total, {
      decimals: 2,
    }),
    controlText: formatters.percentage(salesMetrics.controlPenetration.total, {
      decimals: 2,
    }),
  },
  {
    tagline: parseContent('<b>MORE</b> <lci>PURCHASES</lci>'),
    value: salesMetrics.transactionSizeLift.total,
    descriptionText: parseContent(
      'The <b>lift in average dollars per purchase</b> during the measurement period.'
    ),
    metricName: 'Transaction Size',
    exposedText: formatters.currency(
      salesMetrics.exposedTransactionSize.total,
      { decimals: 2 }
    ),
    controlText: formatters.currency(
      salesMetrics.controlTransactionSize.total,
      { decimals: 2 }
    ),
  },
  {
    tagline: parseContent('<b>MORE</b> <lci>OFTEN</lci>'),
    value: salesMetrics.transactionRateLift.total,
    descriptionText: parseContent(
      'The <b>lift in average number of transactions</b> per shopper during the measurement period.'
    ),
    metricName: 'Transaction Rate',
    exposedText: formatters.number(salesMetrics.exposedTransactionRate.total, {
      decimals: 2,
    }),
    controlText: formatters.number(salesMetrics.controlTransactionRate.total, {
      decimals: 2,
    }),
  },
];
